/**
 * Seed data for Playground Mode.
 * Shapes match the exact API response structures the page components expect.
 */

const now = new Date().toISOString();
const yesterday = new Date(Date.now() - 86400000).toISOString();
const lastWeek = new Date(Date.now() - 7 * 86400000).toISOString();
const nextWeek = new Date(Date.now() + 7 * 86400000).toISOString();
const nextMonth = new Date(Date.now() + 30 * 86400000).toISOString();

// ─── Organization ────────────────────────────────────────────────────
export const PLAYGROUND_ORG = {
    id: 'pg-org-001',
    orgName: 'Playground Studio',
    orgType: 'INTERNAL',
    orgState: 'ACTIVE',
    orgEmail: 'studio@playground.geoplay.io',
    orgOwner: 'pg-user-001',
    primaryContact: 'pg-user-001',
    operatingRegions: ['GLOBAL', 'NA', 'EU', 'APAC', 'SEA'],
    teams: ['Engineering', 'Product', 'Design'],
    lastModifiedDate: now,
    lastModifiedBy: 'Playground Explorer',
};

// ─── Mock Auth User ──────────────────────────────────────────────────
export const PLAYGROUND_USER = {
    uid: 'pg-user-001',
    email: 'explorer@playground.geoplay.io',
    displayName: 'Playground Explorer',
    emailVerified: true,
    accessToken: 'pg-token',
    photoURL: null,
};

// ─── Redux User Details ──────────────────────────────────────────────
// `orgs` must be the ARRAY shape ProtectedRoute reads ([{ orgId, orgState }]) —
// the scalar `orgId` below is kept for the older consumers that still read it.
// Without `orgs`, ProtectedRoute treats the playground user as having no org and
// bounces every protected route to /onboarding, which made playground mode
// unusable after userDetails.orgId became userDetails.orgs.
export const PLAYGROUND_USER_DETAILS = {
    userId: 'pg-user-001',
    userName: 'Playground Explorer',
    orgId: 'pg-org-001',
    orgs: [{ orgId: 'pg-org-001', orgState: 'ACTIVE' }],
    roleId: 'role-admin',
};

// ─── Games ───────────────────────────────────────────────────────────
export const PLAYGROUND_GAMES = [
    {
        id: 'pg-game-001',
        gameClientId: 'pg-game-001',
        gameName: 'Stellar Conquest',
        orgId: 'pg-org-001',
        gameDescription: 'A sci-fi strategy game with interstellar battles and fleet management.',
        gameTeamEmail: 'team@stellarconquest.playground.io',
        gameOwner: 'pg-user-001',
        gameOwnerName: 'Playground Explorer',
        managedRegions: ['GLOBAL', 'NA', 'EU', 'JP'],
        state: 'ACTIVE',
        gameIconUrl: null,
        lastModifiedDate: now,
        lastModifiedBy: 'Playground Explorer',
    },
    {
        id: 'pg-game-002',
        gameClientId: 'pg-game-002',
        gameName: 'Pixel Legends',
        orgId: 'pg-org-001',
        gameDescription: 'A retro-styled RPG with modern mechanics and pixel art aesthetics.',
        gameTeamEmail: 'team@pixellegends.playground.io',
        gameOwner: 'pg-user-001',
        gameOwnerName: 'Playground Explorer',
        managedRegions: ['GLOBAL', 'NA', 'BR', 'IN'],
        state: 'ACTIVE',
        gameIconUrl: null,
        lastModifiedDate: yesterday,
        lastModifiedBy: 'Playground Explorer',
    },
    {
        id: 'pg-game-003',
        gameClientId: 'pg-game-003',
        gameName: 'Arena Blitz',
        orgId: 'pg-org-001',
        gameDescription: 'Fast-paced competitive multiplayer arena with ranked seasons.',
        gameTeamEmail: 'team@arenablitz.playground.io',
        gameOwner: 'pg-user-002',
        gameOwnerName: 'Alex Rivera',
        managedRegions: ['GLOBAL', 'NA', 'KR', 'JP'],
        state: 'ACTIVE',
        gameIconUrl: null,
        lastModifiedDate: lastWeek,
        lastModifiedBy: 'Alex Rivera',
    },
];

// ─── Portfolio Games (for PortfolioDashboard grid) ───────────────────
export const PLAYGROUND_PORTFOLIO_GAMES = [
    {
        id: 'pg-game-001', name: 'Stellar Conquest', genre: 'Strategy',
        platform: 'iOS / Android / Web', region: 'Global', registered: true,
        dau: '1.2M', dauTrend: '+5.8%', revenue: '$890K', revenueTrend: '+12.3%',
        status: 'live', gameId: 'pg-game-001', regionId: 'global', thumbnail: null,
    },
    {
        id: 'pg-game-002', name: 'Pixel Legends', genre: 'RPG',
        platform: 'iOS / Android', region: 'Global', registered: true,
        dau: '680K', dauTrend: '+3.2%', revenue: '$420K', revenueTrend: '+7.1%',
        status: 'live', gameId: 'pg-game-002', regionId: 'global', thumbnail: null,
    },
    {
        id: 'pg-game-003', name: 'Arena Blitz', genre: 'Multiplayer',
        platform: 'iOS / Android / Web / macOS', region: 'Global', registered: true,
        dau: '2.1M', dauTrend: '+9.4%', revenue: '$1.5M', revenueTrend: '+15.6%',
        status: 'live', gameId: 'pg-game-003', regionId: 'global', thumbnail: null,
    },
];

// ─── Remote Configs ──────────────────────────────────────────────────
export const PLAYGROUND_REMOTE_CONFIGS = [
    {
        id: 'pg-rc-001',
        name: 'Welcome to Arena',
        featureId: 'pg-feat-001',
        featureName: 'Inbox',
        minClientVersion: 'v1.0.0',
        maxClientVersion: '1.1.0',
        segmentId: 'pg-seg-001',
        segmentName: 'All Players',
        tagComponent: { tagIds: ['pg-tag-001', 'pg-tag-004'] },
        content: 'Shows a welcome message & starter bonus when Arena unlocks.',
        contentFormat: 'JSON',
        contentLocation: 'REMOTE',
        scheduleComponent: {
            schedules: [
                { epochForExecution: lastWeek, operation: 'START', value: 0, percentage: 1.0 },
                { epochForExecution: nextMonth, operation: 'STOP', value: 0, percentage: 0.0 },
            ],
        },
        repeatableComponent: { cronExpression: '0 0 * * *', timezone: 'UTC', enabled: false, description: 'Daily at midnight', durationInSeconds: 86400 },
        circuitBreakerComponent: { enabled: true, intervalDuration: 1, intervalUnit: 'DAYS', maxOccurrencesPerInterval: 3 },
        state: 'ACTIVE',
        description: 'Shows a welcome message & starter bonus when Arena unlocks.',
        changeDescription: 'Initial configuration for arena welcome',
        versionInfo: {
            version: 1, parentEntityId: 'pg-rc-001-parent',
            auditInfo: { lastModifiedBy: 'explorer@playground.geoplay.io', lastModifiedDate: lastWeek },
            versionDescription: 'Initial version', latest: true,
        },
        platform: { iosEnabled: true, androidEnabled: true },
        gameId: 'pg-game-001',
    },
    {
        id: 'pg-rc-002',
        name: 'Weekend Bonus Battle',
        featureId: 'pg-feat-002',
        featureName: 'Battles',
        minClientVersion: 'v1.1.0',
        maxClientVersion: '1.2.0',
        segmentId: 'pg-seg-002',
        segmentName: 'High Spenders',
        tagComponent: { tagIds: ['pg-tag-001', 'pg-tag-002'] },
        content: 'Weekend 2X bonus battles for engaged players with enhanced rewards.',
        contentFormat: 'JSON',
        contentLocation: 'REMOTE',
        scheduleComponent: {
            schedules: [
                { epochForExecution: nextWeek, operation: 'START', value: 0, percentage: 1.0 },
                { epochForExecution: nextMonth, operation: 'STOP', value: 0, percentage: 0.0 },
            ],
        },
        repeatableComponent: { cronExpression: null, timezone: 'UTC', enabled: false, description: null, durationInSeconds: 0 },
        circuitBreakerComponent: { enabled: false, intervalDuration: 0, intervalUnit: 'HOURS', maxOccurrencesPerInterval: 0 },
        state: 'DRAFT',
        description: 'Weekend 2X bonus battles for engaged players.',
        changeDescription: 'Draft configuration for weekend battles',
        versionInfo: {
            version: 1, parentEntityId: 'pg-rc-002-parent',
            auditInfo: { lastModifiedBy: 'explorer@playground.geoplay.io', lastModifiedDate: yesterday },
            versionDescription: 'Draft version', latest: true,
        },
        platform: { iosEnabled: true, androidEnabled: true },
        gameId: 'pg-game-001',
    },
];

// ─── A/B Tests ───────────────────────────────────────────────────────
export const PLAYGROUND_AB_TESTS = [
    {
        id: 'pg-ab-001',
        name: 'First-Time User Experience',
        description: 'Tests interactive tutorial vs static guide for new players.',
        active: 'Active',
        minClientVersion: 'v1.0.0',
        maxClientVersion: '1.1.0',
        metric: 'Day 1 Retention',
        platform: 'Android',
        targeting: 'First Install',
        featureId: 'pg-feat-001',
        featureName: 'Inbox',
        segmentId: 'pg-seg-001',
        segmentName: 'All Players',
        tagComponent: { tagIds: ['pg-tag-003', 'pg-tag-004'] },
        dateRange: { start: lastWeek, end: nextMonth, duration: '1 month' },
        leadingVariant: { name: 'Tutorial V2', probability: 64, variant: 'B' },
        state: 'ACTIVE',
        versionInfo: {
            version: 1, parentEntityId: 'pg-ab-001-parent',
            auditInfo: { lastModifiedBy: 'explorer@playground.geoplay.io', lastModifiedDate: lastWeek },
            versionDescription: 'Initial version', latest: true,
        },
        gameId: 'pg-game-001',
        icon: 'user-group',
    },
    {
        id: 'pg-ab-002',
        name: 'In-App Purchase Pricing',
        description: 'Tests different price points for gem packs across tiers.',
        active: 'Draft',
        minClientVersion: 'v1.0.0',
        maxClientVersion: '1.2.0',
        metric: 'Conversion Rate',
        platform: 'iOS',
        targeting: 'PlayerLevel > 20',
        featureId: 'pg-feat-002',
        featureName: 'Battles',
        segmentId: 'pg-seg-002',
        segmentName: 'High Spenders',
        tagComponent: { tagIds: ['pg-tag-002'] },
        dateRange: { start: nextWeek, end: nextMonth, duration: '3 weeks' },
        leadingVariant: { name: 'Tier B', probability: 0, variant: 'B' },
        state: 'DRAFT',
        versionInfo: {
            version: 1, parentEntityId: 'pg-ab-002-parent',
            auditInfo: { lastModifiedBy: 'explorer@playground.geoplay.io', lastModifiedDate: yesterday },
            versionDescription: 'Draft version', latest: true,
        },
        gameId: 'pg-game-001',
        icon: 'dollar-sign',
    },
];

// ─── Player Segments ─────────────────────────────────────────────────
export const PLAYGROUND_SEGMENTS = [
    {
        id: 'pg-seg-001',
        regionId: 'global',
        segmentName: 'All Players',
        segmentExpression: 'lifetime_sessions >= 1',
        segmentExpressionTree: JSON.stringify({
            operator: 'AND',
            conditions: [{ field: 'lifetime_sessions', operator: '>=', value: 1 }],
        }),
        clickHouseQueryExpression: 'SELECT * FROM players WHERE lifetime_sessions >= 1',
        state: 'active',
        tagComponent: { tagIds: ['pg-tag-001'] },
        versionInfo: {
            version: 1, parentEntityId: 'pg-seg-001-parent',
            auditInfo: { lastModifiedBy: 'Playground Explorer', lastModifiedDate: lastWeek },
            versionDescription: 'Initial version', latest: true,
        },
        gameId: 'pg-game-001',
    },
    {
        id: 'pg-seg-002',
        regionId: 'global',
        segmentName: 'High Spenders',
        segmentExpression: 'total_spend_30d > 100 AND last_purchase_date <= 30',
        segmentExpressionTree: JSON.stringify({
            operator: 'AND',
            conditions: [
                { field: 'total_spend_30d', operator: '>', value: 100 },
                { field: 'last_purchase_date', operator: '<=', value: 30 },
            ],
        }),
        clickHouseQueryExpression: 'SELECT * FROM players WHERE total_spend_30d > 100 AND last_purchase_date <= 30',
        state: 'draft',
        tagComponent: { tagIds: ['pg-tag-002'] },
        versionInfo: {
            version: 1, parentEntityId: 'pg-seg-002-parent',
            auditInfo: { lastModifiedBy: 'Alex Rivera', lastModifiedDate: yesterday },
            versionDescription: 'Initial version', latest: true,
        },
        gameId: 'pg-game-001',
    },
];

// ─── Client Versions ─────────────────────────────────────────────────
export const PLAYGROUND_CLIENT_VERSIONS = [
    {
        id: 'pg-cv-001',
        // clientVersion is the field ClientVersionDTO actually carries, and what every consumer reads.
        clientVersion: '1.0.0',
        versionInteger: 10000,
        versionName: 'v1.0.0',
        versionCode: '100',
        platform: 'iOS',
        releaseDate: lastWeek,
        minOsVersion: '15.0',
        state: 'ACTIVE',
        description: 'Initial release',
        gameId: 'pg-game-001',
        versionInfo: {
            version: 1, parentEntityId: 'pg-cv-001-parent',
            auditInfo: { lastModifiedBy: 'Playground Explorer', lastModifiedDate: lastWeek },
            versionDescription: 'Initial release', latest: true,
        },
    },
    {
        id: 'pg-cv-002',
        clientVersion: '1.1.0',
        versionInteger: 10100,
        versionName: 'v1.1.0',
        versionCode: '110',
        platform: 'Android',
        releaseDate: yesterday,
        minOsVersion: '12.0',
        state: 'ACTIVE',
        description: 'Bug fixes and performance improvements',
        gameId: 'pg-game-001',
        versionInfo: {
            version: 1, parentEntityId: 'pg-cv-002-parent',
            auditInfo: { lastModifiedBy: 'Playground Explorer', lastModifiedDate: yesterday },
            versionDescription: 'v1.1.0 release', latest: true,
        },
    },
];

// ─── Game Features ───────────────────────────────────────────────────
export const PLAYGROUND_GAME_FEATURES = [
    {
        id: 'pg-feat-001',
        name: 'Inbox',
        description: 'In-game messaging and notification inbox for players.',
        state: 'LIVE',
        gameId: 'pg-game-001',
        versionInfo: {
            version: 1, parentEntityId: 'pg-feat-001-parent',
            auditInfo: { lastModifiedBy: 'Playground Explorer', lastModifiedDate: lastWeek },
            versionDescription: 'Initial version', latest: true,
        },
    },
    {
        id: 'pg-feat-002',
        name: 'Battles',
        description: 'PvP and PvE battle system with matchmaking.',
        state: 'DRAFT',
        gameId: 'pg-game-001',
        versionInfo: {
            version: 1, parentEntityId: 'pg-feat-002-parent',
            auditInfo: { lastModifiedBy: 'Playground Explorer', lastModifiedDate: yesterday },
            versionDescription: 'Draft version', latest: true,
        },
    },
];

// ─── Player Attributes ───────────────────────────────────────────────
export const PLAYGROUND_PLAYER_ATTRIBUTES = [
    {
        id: 'pg-attr-001',
        name: 'player_level',
        description: 'Current player level in the game.',
        dataType: 'number',
        categoryId: 'pg-attrcat-001',
        defaultValue: 1,
        gameId: 'pg-game-001',
    },
    {
        id: 'pg-attr-002',
        name: 'total_spend',
        description: 'Total lifetime spend in USD.',
        dataType: 'number',
        categoryId: 'pg-attrcat-001',
        defaultValue: 0,
        gameId: 'pg-game-001',
    },
];

export const PLAYGROUND_ATTRIBUTE_CATEGORIES = [
    { id: 'pg-attrcat-001', name: 'Player Profile', description: 'Core player profile attributes', gameId: 'pg-game-001' },
    { id: 'pg-attrcat-002', name: 'Monetization', description: 'Spending and purchase attributes', gameId: 'pg-game-001' },
];

// ─── Content Templates ───────────────────────────────────────────────
export const PLAYGROUND_CONTENT_TEMPLATES = [
    {
        id: 'pg-ct-001',
        name: 'Welcome Banner',
        description: 'JSON schema for welcome banner content delivery.',
        schema: JSON.stringify({
            type: 'object',
            properties: {
                title: { type: 'string' },
                body: { type: 'string' },
                imageUrl: { type: 'string' },
                ctaLabel: { type: 'string' },
            },
            required: ['title', 'body'],
        }),
        gameId: 'pg-game-001',
        versionInfo: {
            version: 1, parentEntityId: 'pg-ct-001-parent',
            auditInfo: { lastModifiedBy: 'Playground Explorer', lastModifiedDate: lastWeek },
            versionDescription: 'Initial schema', latest: true,
        },
    },
];

// ─── Tags ────────────────────────────────────────────────────────────
export const PLAYGROUND_TAGS = [
    { id: 'pg-tag-001', name: 'engagement', color: '#6366F1' },
    { id: 'pg-tag-002', name: 'monetization', color: '#F59E0B' },
    { id: 'pg-tag-003', name: 'retention', color: '#10B981' },
    { id: 'pg-tag-004', name: 'onboarding', color: '#3B82F6' },
    { id: 'pg-tag-005', name: 'seasonal', color: '#EF4444' },
];

// ─── Users ───────────────────────────────────────────────────────────
export const PLAYGROUND_USERS = [
    {
        id: 'pg-user-001',
        userName: 'Playground Explorer',
        userId: 'explorer@playground.geoplay.io',
        email: 'explorer@playground.geoplay.io',
        orgUserStates: { 'pg-org-001': 'ACTIVE' },
        roleName: 'Admin',
        roleId: 'role-admin',
        lastModifiedDate: now,
    },
    {
        id: 'pg-user-002',
        userName: 'Alex Rivera',
        userId: 'alex.rivera@playground.geoplay.io',
        email: 'alex.rivera@playground.geoplay.io',
        orgUserStates: { 'pg-org-001': 'ACTIVE' },
        roleName: 'Editor',
        roleId: 'role-editor',
        lastModifiedDate: yesterday,
    },
    {
        id: 'pg-user-003',
        userName: 'Sam Chen',
        userId: 'sam.chen@playground.geoplay.io',
        email: 'sam.chen@playground.geoplay.io',
        orgUserStates: { 'pg-org-001': 'ACTIVE' },
        roleName: 'Viewer',
        roleId: 'role-viewer',
        lastModifiedDate: lastWeek,
    },
];

// ─── Roles ───────────────────────────────────────────────────────────
export const PLAYGROUND_ROLES = [
    { id: 'role-admin', name: 'Admin', description: 'Full access to all features' },
    { id: 'role-editor', name: 'Editor', description: 'Can create and edit content' },
    { id: 'role-viewer', name: 'Viewer', description: 'Read-only access' },
];

// ─── Platforms ───────────────────────────────────────────────────────
export const PLAYGROUND_PLATFORMS = [
    { id: 'platform-ios', name: 'iOS' },
    { id: 'platform-android', name: 'Android' },
    { id: 'platform-web', name: 'Web' },
    { id: 'platform-macos', name: 'macOS' },
];

// ─── Metrics Catalogue ──────────────────────────────────────────────
export const PLAYGROUND_METRICS = [
    { id: 'metric-001', name: 'Day 1 Retention', category: 'Retention', unit: '%' },
    { id: 'metric-002', name: 'Day 7 Retention', category: 'Retention', unit: '%' },
    { id: 'metric-003', name: 'ARPU', category: 'Revenue', unit: '$' },
    { id: 'metric-004', name: 'Conversion Rate', category: 'Revenue', unit: '%' },
    { id: 'metric-005', name: 'Session Length', category: 'Engagement', unit: 'min' },
    { id: 'metric-006', name: 'DAU', category: 'Engagement', unit: 'users' },
];


// ─── Game Configs ────────────────────────────────────────────────────
const CONFIG_FILE_SEED = [
    ['sample-configs/economy/store-offers.json', 'ECONOMY', 'Storefront bundles, prices and their display order.'],
    ['sample-configs/economy/currency-config.json', 'ECONOMY', 'Soft and hard currency caps, conversion and grant rules.'],
    ['sample-configs/economy/gacha-tables.json', 'ECONOMY', 'Drop rates and pity counters per banner.'],
    ['sample-configs/progression/battle-pass.json', 'PROGRESSION', 'Tier rewards and XP curve for the current season.'],
    ['sample-configs/progression/xp-curve.json', 'PROGRESSION', ''],
    ['sample-configs/liveops/daily-rewards.json', 'LIVEOPS', 'Login streak rewards.'],
    ['sample-configs/liveops/event-schedule.json', 'LIVEOPS', 'Which live events run, and when.'],
    ['sample-configs/competitive/matchmaking.json', 'CORE', 'Skill bands, wait-time tolerance and party rules.'],
    ['sample-configs/ui/onboarding-flow.yaml', 'UI', 'First-session tutorial steps.'],
    ['sample-configs/ui/hud-layout.json', 'UI', ''],
    ['sample-configs/competitive/ranked-seasons.json', 'CORE', 'Season length, reset rules and placement matches.'],
    ['sample-configs/social/guilds.json', 'SOCIAL', 'Guild sizes, perks and join rules.'],
    ['sample-configs/social/chat-filters.json', 'SOCIAL', ''],
    ['sample-configs/tuning/combat-balance.json', 'TUNING', 'Damage curves and cooldowns.'],
    ['sample-configs/tuning/ai-difficulty.json', 'TUNING', 'Per-tier bot behaviour.'],
];

const daysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString();

// Enough files carry tags to make the tag filter and the tag picker worth looking at, and enough
// carry none to show what an untagged file looks like beside them.
const CONFIG_FILE_TAGS = [
    ['pg-tag-002'], ['pg-tag-002', 'pg-tag-005'], ['pg-tag-002'],
    ['pg-tag-001', 'pg-tag-005'], [], ['pg-tag-003'], ['pg-tag-005'],
    [], ['pg-tag-004'], [], ['pg-tag-003'], ['pg-tag-001'], [], [], ['pg-tag-001'],
];

export const PLAYGROUND_CONFIG_FILES = CONFIG_FILE_SEED.map(([name, system, description], i) => ({
    id: `pg-cfg-${String(i + 1).padStart(3, '0')}`,
    name,
    format: name.endsWith('.yaml') ? 'YAML' : 'JSON',
    system,
    description,
    tagComponent: { tagIds: CONFIG_FILE_TAGS[i] || [] },
    origin: i % 4 === 0 ? 'MANUAL' : 'IMPORTED',
    archived: false,
    gameId: 'pg-game-001',
    createdBy: 'Playground Explorer',
    createdDate: daysAgo(30 - i),
    lastModifiedBy: 'Playground Explorer',
    lastModifiedDate: daysAgo(i % 7),
}));

const branchVersion = (id, version, description) => ({
    version,
    parentEntityId: `${id}-parent`,
    isLatest: true,
    versionDescription: description,
    auditInfo: { lastModifiedBy: 'Playground Explorer', lastModifiedDate: daysAgo(1) },
    createdBy: 'Playground Explorer',
    createdDate: daysAgo(10),
});

// Each file gets a live base; a few grow a targeted override so the list shows real counts and the
// tree has something to nest.
export const PLAYGROUND_CONFIG_BRANCHES = PLAYGROUND_CONFIG_FILES.flatMap((file, i) => {
    const baseId = `${file.id}-base`;
    const rows = [{
        id: baseId,
        fileId: file.id,
        branchParentId: null,
        isBase: true,
        name: 'Base',
        description: 'The reference every branch inherits from — never served itself.',
        state: i === 4 ? 'DRAFT' : 'ACTIVE',
        priority: 0,
        segmentIds: [],
        content: JSON.stringify({ enabled: true }, null, 2),
        versionInfo: branchVersion(baseId, i === 4 ? 1 : 3, 'Published'),
    }];

    if (i % 3 === 0) {
        const childId = `${file.id}-sea`;
        rows.push({
            id: childId,
            fileId: file.id,
            branchParentId: `${baseId}-parent`,
            isBase: false,
            name: 'South-East Asia',
            description: 'Regional pricing for the SEA markets.',
            state: 'ACTIVE',
            priority: 10,
            // The LINEAGE id, as the UI writes it — not the version document's own id.
            segmentIds: ['pg-seg-001-parent'],
            rolloutPercentage: 25,
            content: JSON.stringify({ enabled: true, priceMultiplier: 0.6 }, null, 2),
            versionInfo: branchVersion(childId, 2, 'Edit'),
        });
    }

    if (i % 4 === 1) {
        const draftId = `${file.id}-draft`;
        rows.push({
            id: draftId,
            fileId: file.id,
            branchParentId: `${baseId}-parent`,
            isBase: false,
            name: 'Returning spenders',
            description: '',
            state: 'DRAFT',
            priority: 5,
            segmentIds: ['pg-seg-002-parent'],
            content: JSON.stringify({ enabled: true, bonus: 2 }, null, 2),
            versionInfo: branchVersion(draftId, 1, 'Initial version'),
        });
    }

    return rows;
});

// ─── Seed function ───────────────────────────────────────────────────
const SEED_VERSION = '6'; // Bump this when seed data shape changes to force re-seed

export function seedPlaygroundData() {
    if (sessionStorage.getItem('pg_seeded') === SEED_VERSION) {
        return; // Already seeded this session with current version
    }

    sessionStorage.setItem('pg_org', JSON.stringify(PLAYGROUND_ORG));
    sessionStorage.setItem('pg_games', JSON.stringify(PLAYGROUND_GAMES));
    sessionStorage.setItem('pg_portfolio_games', JSON.stringify(PLAYGROUND_PORTFOLIO_GAMES));
    sessionStorage.setItem('pg_remote_configs', JSON.stringify(PLAYGROUND_REMOTE_CONFIGS));
    sessionStorage.setItem('pg_ab_tests', JSON.stringify(PLAYGROUND_AB_TESTS));
    sessionStorage.setItem('pg_segments', JSON.stringify(PLAYGROUND_SEGMENTS));
    sessionStorage.setItem('pg_client_versions', JSON.stringify(PLAYGROUND_CLIENT_VERSIONS));
    sessionStorage.setItem('pg_game_features', JSON.stringify(PLAYGROUND_GAME_FEATURES));
    sessionStorage.setItem('pg_player_attributes', JSON.stringify(PLAYGROUND_PLAYER_ATTRIBUTES));
    sessionStorage.setItem('pg_attribute_categories', JSON.stringify(PLAYGROUND_ATTRIBUTE_CATEGORIES));
    sessionStorage.setItem('pg_content_templates', JSON.stringify(PLAYGROUND_CONTENT_TEMPLATES));
    sessionStorage.setItem('pg_tags', JSON.stringify(PLAYGROUND_TAGS));
    sessionStorage.setItem('pg_users', JSON.stringify(PLAYGROUND_USERS));
    sessionStorage.setItem('pg_invitations', JSON.stringify([]));
    sessionStorage.setItem('pg_platforms', JSON.stringify(PLAYGROUND_PLATFORMS));
    sessionStorage.setItem('pg_roles', JSON.stringify(PLAYGROUND_ROLES));
    sessionStorage.setItem('pg_metrics', JSON.stringify(PLAYGROUND_METRICS));
    sessionStorage.setItem('pg_config_files', JSON.stringify(PLAYGROUND_CONFIG_FILES));
    sessionStorage.setItem('pg_config_branches', JSON.stringify(PLAYGROUND_CONFIG_BRANCHES));
    sessionStorage.setItem('pg_id_counter', '200');
    sessionStorage.setItem('pg_seeded', SEED_VERSION);
}
